"use client";
import { Track } from "@/src/lib/tracking";

import { ensureOpenReplayTrackerStarted, getTracker } from "@utils/openreplay";

import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/app/global-error";
import { rootStore } from "@store/rootStore";
import { useEffect } from "react";

// import NextError from "next/error";

const I18n = I18nUtil.createScoped(Translations);

const MAX_STACK_CHARS = 8000;
const MAX_SEARCH_CHARS = 2000;

function truncate(s: string, max: number): string {
  if (s.length <= max) {
    return s;
  }
  return s.slice(0, max) + "…";
}

function redactSecrets(raw: string): string {
  // Best-effort redaction for common token patterns; keep it minimal to avoid false positives.
  return raw
    .replace(/glpat-[A-Za-z0-9._-]+/g, "glpat-REDACTED")
    .replace(/Bearer\s+[A-Za-z0-9._-]+/gi, "Bearer REDACTED")
    .replace(
      /(token|access_token|id_token|refresh_token|api_key|apikey)=([^&\s]+)/gi,
      "$1=REDACTED",
    );
}

async function reportGlobalErrorToOpenReplay(
  error: Error & { digest?: string },
) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    await ensureOpenReplayTrackerStarted();
    const tracker = getTracker();
    if (!tracker) {
      return;
    }

    let pathname: string | undefined;
    let search: string | undefined;
    try {
      pathname = window.location?.pathname;
      search = window.location?.search;
    } catch {
      // ignore
    }

    const stack = error?.stack
      ? truncate(redactSecrets(String(error.stack)), MAX_STACK_CHARS)
      : undefined;
    const message = error?.message
      ? redactSecrets(String(error.message))
      : "Unknown error";

    let isLoggedIn: boolean | undefined;
    let ownerId: string | undefined;
    try {
      isLoggedIn = (rootStore as any)?.login?.isLoggedIn;
      const id = (rootStore as any)?.user?.ownerInfo?.id;
      ownerId = id != null ? String(id) : undefined;
    } catch {
      // ignore
    }

    const metadata: Record<string, unknown> = {
      source: "global-error",
      ts: new Date().toISOString(),
      digest: error?.digest,
      name: error?.name,
      message: message,
      stack: stack,
      route: pathname,
      search: search
        ? truncate(redactSecrets(search), MAX_SEARCH_CHARS)
        : undefined,
      userAgent:
        typeof navigator !== "undefined" ? navigator.userAgent : undefined,
      locale: (() => {
        try {
          return (I18nUtil as any).getLocale?.();
        } catch {
          return undefined;
        }
      })(),
      login: {
        isLoggedIn,
        ownerId,
      },
    };

    // Prefer handleError so OpenReplay treats it as an error, fallback to custom event.
    try {
      tracker.handleError?.(error, metadata);
      return;
    } catch {
      // fallback below
    }
    tracker.event?.("global-error", metadata);
  } catch (e) {
    Track.log("warn", "global-error", "openreplay report failed", {
      cause: String(e),
    });
  }
}

export default function GlobalError({
  error,
}: {
  error: Error & { digest?: string };
}) {
  useEffect(() => {
    // ⚠️ 这里**不能**用 `Track.error` —— 它内部会调 `tracker.handleError`，
    // 而下一行的 `reportGlobalErrorToOpenReplay` 已经在报同一个错误（且 metadata 更全）。
    // 两条都发 = 同一个错误在 OpenReplay 里出现两次，且第一条更贫瘠（规范 §5 去重）。
    Track.log("error", "global-error", "fatal error", {
      digest: error.digest,
    });
    void reportGlobalErrorToOpenReplay(error);
  }, [error]);

  return (
    <html
      aria-label="Global Error html"
      data-openreplay-label="infra_globalerror_html"
      id="sem-global-error-html"
    >
      <body
        aria-label="Global Error body"
        data-openreplay-label="infra_globalerror_body"
        id="sem-global-error-body"
      >
        {/* `NextError` is the default Next.js error page component. Its type
        definition requires a `statusCode` prop. However, since the App Router
        does not expose status codes for errors, we simply pass 0 to render a
        generic error message. */}
        {/* <div id="global-error">
          <NextError statusCode={0} className="notranslate global-error" />
        </div> */}
        <div id="global-error">
          <div className="w-full bg-background_solid_secondary flex flex-col items-center justify-center h-screen">
            <div className="heading_m text-content_primary">
              {I18n.t("Something Went Wrong")}
            </div>
            <div className=" body_l text-content_tertiary mt-2">
              {I18n.t("Please refresh the page.")}
            </div>
            <button
              aria-label="Global Error"
              data-openreplay-label="infra_globalerror_click"
              id="btn-global-error"
              className="button button_m md_button text-content_inverse_primary mt-8 bg-background_white_overlay100"
              onClick={() => window.location.reload()}
            >
              {I18n.t("Refresh")}
            </button>
          </div>
        </div>
      </body>
    </html>
  );
}
